'use client';

import Image from 'next/image';
import {
  useRef,
  useState,
  type ChangeEvent,
  type Dispatch,
  type DragEvent,
  type KeyboardEvent,
  type PointerEvent,
  type SetStateAction,
} from 'react';
import type { Product } from '@/lib/shopify/types';
import {
  createDefaultCustomDesign,
  getArtworkKindLabel,
  getDesignSideLabel,
  getPrintAreaForSide,
  getTextAlignOption,
  getTextFontOption,
  hasCustomDesign,
  isImageArtwork,
  sideHasDesign,
  TEXT_ALIGN_OPTIONS,
  TEXT_FONT_OPTIONS,
  type CustomDesignState,
  type CustomizerSide,
  type TextAlignId,
  type TextFontId,
} from '@/lib/customizer';

const ALLOWED_EXTENSIONS = ['png', 'jpg', 'jpeg', 'webp', 'svg', 'pdf', 'ai', 'eps'];
const MAX_SIZE = 25 * 1024 * 1024;
const MIN_PRINT_IMAGE_DIMENSION = 1000;
const RASTER_IMAGE_EXTENSIONS = new Set(['png', 'jpg', 'jpeg', 'webp']);
const TRANSPARENCY_CHECK_EXTENSIONS = new Set(['png', 'webp']);
const PRINT_FILE_EXTENSIONS = new Set(['pdf', 'ai', 'eps']);
const TEXT_COLORS = ['#111111', '#ffffff', '#dc2626', '#2563eb', '#16a34a', '#f59e0b'];
type DragTarget = 'artwork' | 'text';
type PendingArtworkFiles = Partial<Record<CustomizerSide, File | null>>;

interface UploadedArtwork {
  url: string;
  filename: string;
}

interface ProductCustomizerProps {
  product: Product;
  design: CustomDesignState;
  previewImageUrl?: string;
  previewImageAlt?: string;
  sideAvailability?: Record<CustomizerSide, boolean>;
  sideAvailabilityNotes?: Partial<Record<CustomizerSide, string>>;
  deferUpload?: boolean;
  pendingArtworkFiles?: PendingArtworkFiles;
  onPendingArtworkChange?: (side: CustomizerSide, file: File | null) => void;
  onDuplicatePendingArtwork?: (fromSide: CustomizerSide, toSide: CustomizerSide) => void;
  onClearPendingArtwork?: () => void;
  onChange: Dispatch<SetStateAction<CustomDesignState>>;
  onUploadingChange?: (isUploading: boolean) => void;
}

interface OverlayProps {
  design: CustomDesignState;
  side?: CustomizerSide;
  showBoundary?: boolean;
  editable?: boolean;
  dragTarget?: DragTarget;
  onArtworkPositionChange?: (x: number, y: number) => void;
  onTextPositionChange?: (x: number, y: number) => void;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function formatSize(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)}KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
}

function validateFile(file: File): string | null {
  const ext = getFileExtension(file.name);
  if (!ALLOWED_EXTENSIONS.includes(ext)) {
    return `File type .${ext} is not supported. Use PNG, JPG, WEBP, SVG, PDF, AI, or EPS.`;
  }
  if (file.size > MAX_SIZE) {
    return 'File too large. Maximum 25MB.';
  }
  return null;
}

function getFileExtension(filename: string): string {
  return filename.split('.').pop()?.toLowerCase() || '';
}

function loadImageFromFile(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new window.Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Image could not be checked'));
    };
    image.src = url;
  });
}

function imageHasTransparentPixels(image: HTMLImageElement): boolean {
  const sampleWidth = Math.min(320, image.naturalWidth || image.width);
  const sampleHeight = Math.min(320, image.naturalHeight || image.height);
  if (sampleWidth <= 0 || sampleHeight <= 0) return false;

  const canvas = document.createElement('canvas');
  canvas.width = sampleWidth;
  canvas.height = sampleHeight;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return false;

  ctx.drawImage(image, 0, 0, sampleWidth, sampleHeight);
  const pixels = ctx.getImageData(0, 0, sampleWidth, sampleHeight).data;
  for (let i = 3; i < pixels.length; i += 4) {
    if (pixels[i] < 250) return true;
  }
  return false;
}

async function getArtworkWarnings(file: File): Promise<string[]> {
  const ext = getFileExtension(file.name);
  const warnings: string[] = [];

  if (PRINT_FILE_EXTENSIONS.has(ext)) {
    return ['Print file uploaded. The preview may show a placeholder, but the production file will be attached to the order.'];
  }

  if (ext === 'svg') {
    return ['Vector artwork uploaded. Complex SVG effects may preview differently from the final print file.'];
  }

  if (!RASTER_IMAGE_EXTENSIONS.has(ext)) return warnings;

  try {
    const image = await loadImageFromFile(file);
    const width = image.naturalWidth || image.width;
    const height = image.naturalHeight || image.height;

    if (width < MIN_PRINT_IMAGE_DIMENSION || height < MIN_PRINT_IMAGE_DIMENSION) {
      warnings.push(`Low resolution image (${width} x ${height}px). For best print results, upload artwork at least ${MIN_PRINT_IMAGE_DIMENSION} x ${MIN_PRINT_IMAGE_DIMENSION}px.`);
    }

    if (TRANSPARENCY_CHECK_EXTENSIONS.has(ext) && !imageHasTransparentPixels(image)) {
      warnings.push('This image has a solid background. Use transparent PNG or WEBP artwork if you do not want a box around the print.');
    }

    if (ext === 'jpg' || ext === 'jpeg') {
      warnings.push('JPG files usually have a solid background. Use transparent PNG artwork if the background should not print.');
    }
  } catch {
    warnings.push('Image quality could not be checked. Please make sure the artwork is high resolution before ordering.');
  }

  return warnings;
}

async function uploadCustomizerArtwork(file: File): Promise<UploadedArtwork> {
  const formData = new FormData();
  formData.append('file', file);

  const res = await fetch('/api/upload', {
    method: 'POST',
    body: formData,
  });
  const data = await res.json() as { url?: string; filename?: string; error?: string };

  if (!res.ok || !data.url) {
    throw new Error(data.error || 'Artwork upload failed. Please try again.');
  }

  return {
    url: data.url,
    filename: data.filename || file.name,
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function normalizeNumber(value: unknown, fallback: number, min: number, max: number): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return fallback;
  return clamp(value, min, max);
}

function normalizeTextColor(value: unknown, fallback: string): string {
  if (typeof value !== 'string') return fallback;
  return /^#[0-9a-fA-F]{6}$/.test(value) ? value : fallback;
}

function normalizeString(value: unknown, fallback: string, maxLength: number): string {
  if (typeof value !== 'string') return fallback;
  return value.slice(0, maxLength);
}

function normalizeStringList(value: unknown, maxItems: number, maxLength: number): string[] {
  if (!Array.isArray(value)) return [];
  return value
    .filter((item): item is string => typeof item === 'string')
    .map((item) => item.slice(0, maxLength))
    .filter(Boolean)
    .slice(0, maxItems);
}

function isTemporaryArtworkUrl(value: string): boolean {
  return value.startsWith('blob:');
}

function storageKeyForProduct(productId: string): string {
  return `oktshirt-custom-design:${productId}`;
}

function canUseStorage(): boolean {
  try {
    return typeof window !== 'undefined' && Boolean(window.localStorage);
  } catch {
    return false;
  }
}

function normalizeSavedDesign(
  value: unknown,
  currentDesign: CustomDesignState
): CustomDesignState | null {
  if (!isRecord(value) || !isRecord(value.sides)) return null;

  const blank = createDefaultCustomDesign(currentDesign.profile);
  const normalizeSide = (
    sideValue: unknown,
    blankSide: CustomDesignState['sides']['front']
  ): CustomDesignState['sides']['front'] => {
    if (!isRecord(sideValue)) return blankSide;
    const textFont = TEXT_FONT_OPTIONS.some((font) => font.id === sideValue.textFont)
      ? sideValue.textFont as TextFontId
      : blankSide.textFont;
    const textAlign = TEXT_ALIGN_OPTIONS.some((align) => align.id === sideValue.textAlign)
      ? sideValue.textAlign as TextAlignId
      : blankSide.textAlign;
    const artworkUrl = normalizeString(sideValue.artworkUrl, blankSide.artworkUrl, 2048);
    const hasTemporaryArtwork = isTemporaryArtworkUrl(artworkUrl);
    return {
      ...blankSide,
      ...sideValue,
      artworkUrl: hasTemporaryArtwork ? blankSide.artworkUrl : artworkUrl,
      artworkFilename: hasTemporaryArtwork ? blankSide.artworkFilename : normalizeString(sideValue.artworkFilename, blankSide.artworkFilename, 180),
      artworkKind: hasTemporaryArtwork
        ? blankSide.artworkKind
        : sideValue.artworkKind === 'image' || sideValue.artworkKind === 'file' ? sideValue.artworkKind : blankSide.artworkKind,
      artworkWarnings: hasTemporaryArtwork ? [] : normalizeStringList(sideValue.artworkWarnings, 4, 180),
      x: normalizeNumber(sideValue.x, blankSide.x, 0, 100),
      y: normalizeNumber(sideValue.y, blankSide.y, 0, 100),
      scale: normalizeNumber(sideValue.scale, blankSide.scale, 20, 140),
      rotation: normalizeNumber(sideValue.rotation, blankSide.rotation, -45, 45),
      text: normalizeString(sideValue.text, blankSide.text, 120),
      textX: normalizeNumber(sideValue.textX, blankSide.textX, 0, 100),
      textY: normalizeNumber(sideValue.textY, blankSide.textY, 0, 100),
      textRotation: normalizeNumber(sideValue.textRotation, blankSide.textRotation, -45, 45),
      textSize: normalizeNumber(sideValue.textSize, blankSide.textSize, 12, 48),
      textColor: normalizeTextColor(sideValue.textColor, blankSide.textColor),
      textFont,
      textBold: typeof sideValue.textBold === 'boolean' ? sideValue.textBold : blankSide.textBold,
      textItalic: typeof sideValue.textItalic === 'boolean' ? sideValue.textItalic : blankSide.textItalic,
      textAlign,
    };
  };

  return {
    ...blank,
    activeSide: value.activeSide === 'back' ? 'back' : 'front',
    sides: {
      front: normalizeSide(value.sides.front, blank.sides.front),
      back: normalizeSide(value.sides.back, blank.sides.back),
    },
    instructions: normalizeString(value.instructions, blank.instructions, 240),
  };
}

function stripTemporaryArtwork(design: CustomDesignState): { design: CustomDesignState; removedArtwork: boolean } {
  let removedArtwork = false;
  const sides = { ...design.sides };

  (['front', 'back'] as CustomizerSide[]).forEach((side) => {
    if (!isTemporaryArtworkUrl(sides[side].artworkUrl)) return;
    removedArtwork = true;
    sides[side] = {
      ...sides[side],
      artworkUrl: '',
      artworkFilename: '',
      artworkKind: '',
      artworkWarnings: [],
      x: 50,
      y: 48,
      scale: 58,
      rotation: 0,
    };
  });

  return { design: { ...design, sides }, removedArtwork };
}

export function CustomizerPreviewOverlay({
  design,
  side = design.activeSide,
  showBoundary = false,
  editable = false,
  dragTarget,
  onArtworkPositionChange,
  onTextPositionChange,
}: OverlayProps) {
  const profile = design.profile;
  const printArea = getPrintAreaForSide(profile, side);
  const sideDesign = design.sides[side];
  const draggingRef = useRef(false);
  const hasArtwork = Boolean(sideDesign.artworkUrl);
  const hasText = Boolean(sideDesign.text.trim());

  const areaStyle = {
    left: `${printArea.centerX - printArea.width / 2}%`,
    top: `${printArea.centerY - printArea.height / 2}%`,
    width: `${printArea.width}%`,
    height: `${printArea.height}%`,
  };

  const updateFromPointer = (event: PointerEvent<HTMLDivElement>) => {
    if (!editable) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const x = clamp(((event.clientX - rect.left) / rect.width) * 100, 0, 100);
    const y = clamp(((event.clientY - rect.top) / rect.height) * 100, 0, 100);
    const canMoveArtwork = hasArtwork && onArtworkPositionChange;
    const canMoveText = hasText && onTextPositionChange;
    const activeDragTarget = dragTarget === 'text' && canMoveText
      ? 'text'
      : dragTarget === 'artwork' && canMoveArtwork
        ? 'artwork'
        : canMoveArtwork
          ? 'artwork'
          : canMoveText
            ? 'text'
            : null;

    if (activeDragTarget === 'artwork' && onArtworkPositionChange) {
      onArtworkPositionChange(x, y);
      return;
    }
    if (activeDragTarget === 'text' && onTextPositionChange) {
      onTextPositionChange(x, y);
    }
  };

  const handlePointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (!editable || !sideHasDesign(sideDesign)) return;
    draggingRef.current = true;
    try {
      event.currentTarget.setPointerCapture(event.pointerId);
    } catch {
      // Dragging still works when pointer capture is unavailable in automation or older browsers.
    }
    updateFromPointer(event);
  };

  const handlePointerMove = (event: PointerEvent<HTMLDivElement>) => {
    if (!draggingRef.current) return;
    updateFromPointer(event);
  };

  const handlePointerUp = () => {
    draggingRef.current = false;
  };

  const textFont = getTextFontOption(sideDesign.textFont);
  const textAlign = getTextAlignOption(sideDesign.textAlign);
  const artworkLabel = getArtworkKindLabel(sideDesign);

  return (
    <div
      className="absolute"
      style={areaStyle}
      aria-label={`${getDesignSideLabel(design, side)} ${printArea.label}`}
    >
      <div
        data-customizer-preview-area={side}
        className={`relative h-full w-full overflow-hidden ${editable && sideHasDesign(sideDesign) ? 'cursor-grab touch-none active:cursor-grabbing' : ''}`}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
      >
        {showBoundary && (
          <div className="pointer-events-none absolute inset-0 border border-dashed border-black/45 bg-white/5">
            {!sideHasDesign(sideDesign) && (
              <span className="absolute left-2 top-2 bg-white/90 px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-black">
                {printArea.label}
              </span>
            )}
          </div>
        )}

        {hasArtwork && sideDesign.artworkKind === 'image' && (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={sideDesign.artworkUrl}
            alt={sideDesign.artworkFilename || 'Uploaded artwork'}
            className="pointer-events-none absolute block select-none object-contain drop-shadow-sm"
            style={{
              left: `${sideDesign.x}%`,
              top: `${sideDesign.y}%`,
              width: `${sideDesign.scale}%`,
              maxWidth: 'none',
              transform: `translate(-50%, -50%) rotate(${sideDesign.rotation}deg)`,
              transformOrigin: 'center center',
            }}
            draggable={false}
          />
        )}

        {hasArtwork && sideDesign.artworkKind === 'file' && (
          <div
            className="pointer-events-none absolute flex min-h-12 min-w-24 select-none flex-col items-center justify-center border border-black/20 bg-white/90 px-3 py-2 text-center text-[10px] font-semibold uppercase tracking-wide text-black shadow-sm"
            style={{
              left: `${sideDesign.x}%`,
              top: `${sideDesign.y}%`,
              width: `${Math.max(30, sideDesign.scale)}%`,
              transform: `translate(-50%, -50%) rotate(${sideDesign.rotation}deg)`,
              transformOrigin: 'center center',
            }}
          >
            <span>{artworkLabel}</span>
            {sideDesign.artworkFilename && (
              <span className="mt-1 max-w-full truncate text-[9px] font-medium normal-case tracking-normal text-[var(--color-text-secondary)]">
                {sideDesign.artworkFilename}
              </span>
            )}
          </div>
        )}

        {hasText && (
          <div
            data-customizer-text-preview={side}
            className="pointer-events-none absolute max-w-[92%] select-none whitespace-pre-wrap break-words leading-tight drop-shadow-sm"
            style={{
              left: `${sideDesign.textX}%`,
              top: `${sideDesign.textY}%`,
              width: '92%',
              color: sideDesign.textColor,
              fontFamily: textFont.css,
              fontSize: `${sideDesign.textSize}px`,
              fontStyle: sideDesign.textItalic ? 'italic' : 'normal',
              fontWeight: sideDesign.textBold ? 700 : 400,
              textAlign: textAlign.id,
              transform: `translate(-50%, -50%) rotate(${sideDesign.textRotation}deg)`,
              WebkitTextStroke: sideDesign.textColor === '#ffffff' ? '0.4px rgba(0,0,0,0.45)' : undefined,
            }}
          >
            {sideDesign.text}
          </div>
        )}
      </div>
    </div>
  );
}

export default function ProductCustomizer({
  product,
  design,
  previewImageUrl,
  previewImageAlt,
  sideAvailability,
  sideAvailabilityNotes,
  deferUpload = false,
  pendingArtworkFiles,
  onPendingArtworkChange,
  onDuplicatePendingArtwork,
  onClearPendingArtwork,
  onChange,
  onUploadingChange,
}: ProductCustomizerProps) {
  const [isDraggingFile, setIsDraggingFile] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState('');
  const [moveTarget, setMoveTarget] = useState<DragTarget>('artwork');
  const [savedDesignAvailable, setSavedDesignAvailable] = useState(() => {
    if (!canUseStorage()) return false;
    return Boolean(window.localStorage.getItem(storageKeyForProduct(product.id)));
  });
  const [saveMessage, setSaveMessage] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const availableSides: Record<CustomizerSide, boolean> = {
    front: sideAvailability?.front ?? true,
    back: design.profile.supportsBack && (sideAvailability?.back ?? design.profile.supportsBack),
  };
  const fallbackSide: CustomizerSide = availableSides.front ? 'front' : availableSides.back ? 'back' : 'front';
  const activeSide = availableSides[design.activeSide] ? design.activeSide : fallbackSide;
  const activeDesign = design.sides[activeSide];
  const activePrintArea = getPrintAreaForSide(design.profile, activeSide);
  const otherSide: CustomizerSide = activeSide === 'front' ? 'back' : 'front';
  const canEditActiveSide = availableSides[activeSide];
  const unavailableActiveSideNote = sideAvailabilityNotes?.[activeSide] || `${getDesignSideLabel(design, activeSide)} design is unavailable for this product setup.`;
  const canMoveArtwork = canEditActiveSide && Boolean(activeDesign.artworkUrl);
  const canMoveText = canEditActiveSide && Boolean(activeDesign.text.trim());
  const activeMoveTarget: DragTarget | undefined = moveTarget === 'text' && canMoveText
    ? 'text'
    : moveTarget === 'artwork' && canMoveArtwork
      ? 'artwork'
      : canMoveArtwork
        ? 'artwork'
        : canMoveText
          ? 'text'
          : undefined;

  const updateDesign = (patch: Partial<CustomDesignState>) => {
    onChange((current) => ({ ...current, ...patch }));
  };

  const updateSide = (patch: Partial<typeof activeDesign>) => {
    onChange((current) => ({
      ...current,
      activeSide,
      sides: {
        ...current.sides,
        [activeSide]: {
          ...current.sides[activeSide],
          ...patch,
        },
      },
    }));
  };

  const uploadFile = async (file: File) => {
    if (isUploading) return;
    if (!canEditActiveSide) {
      setError(unavailableActiveSideNote);
      return;
    }

    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }

    setError('');
    const artworkWarnings = await getArtworkWarnings(file);

    if (deferUpload) {
      const localUrl = URL.createObjectURL(file);
      updateSide({
        artworkUrl: localUrl,
        artworkFilename: file.name,
        artworkKind: isImageArtwork(file.name) ? 'image' : 'file',
        artworkWarnings,
        x: 50,
        y: 48,
        scale: isImageArtwork(file.name) ? 58 : 44,
        rotation: 0,
      });
      onPendingArtworkChange?.(activeSide, file);
      setMoveTarget('artwork');
      return;
    }

    setIsUploading(true);
    onUploadingChange?.(true);

    try {
      const data = await uploadCustomizerArtwork(file);

      updateSide({
        artworkUrl: data.url,
        artworkFilename: data.filename || file.name,
        artworkKind: isImageArtwork(data.filename || file.name) ? 'image' : 'file',
        artworkWarnings,
        x: 50,
        y: 48,
        scale: isImageArtwork(data.filename || file.name) ? 58 : 44,
        rotation: 0,
      });
      setMoveTarget('artwork');
    } catch {
      setError('Upload failed. Please try again.');
    } finally {
      setIsUploading(false);
      onUploadingChange?.(false);
    }
  };

  const handleFileSelect = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) uploadFile(file);
    if (inputRef.current) inputRef.current.value = '';
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDraggingFile(false);
    const file = event.dataTransfer.files[0];
    if (file) uploadFile(file);
  };

  const handleUploadKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    if (isUploading || !canEditActiveSide) return;
    inputRef.current?.click();
  };

  const removeSideDesign = () => {
    const blank = createDefaultCustomDesign(design.profile).sides[activeSide];
    onPendingArtworkChange?.(activeSide, null);
    updateSide(blank);
  };

  const removeArtwork = () => {
    updateSide({
      artworkUrl: '',
      artworkFilename: '',
      artworkKind: '',
      artworkWarnings: [],
      x: 50,
      y: 48,
      scale: 58,
      rotation: 0,
    });
    onPendingArtworkChange?.(activeSide, null);
    if (activeDesign.text.trim()) setMoveTarget('text');
  };

  const clearText = () => {
    updateSide({ text: '' });
    if (activeDesign.artworkUrl) setMoveTarget('artwork');
  };

  const duplicateActiveSideToOtherSide = () => {
    if (!availableSides[otherSide] || !sideHasDesign(design.sides[activeSide])) return;
    onDuplicatePendingArtwork?.(activeSide, otherSide);
    onChange((current) => {
      if (!current.profile.supportsBack || !sideHasDesign(current.sides[activeSide])) return current;
      return {
        ...current,
        activeSide: otherSide,
        sides: {
          ...current.sides,
          [otherSide]: { ...current.sides[activeSide] },
        },
      };
    });
  };

  const showSaveMessage = (message: string) => {
    setSaveMessage(message);
    window.setTimeout(() => setSaveMessage(''), 1800);
  };

  const saveDesign = async () => {
    if (!canUseStorage()) {
      showSaveMessage('Storage unavailable');
      return;
    }

    let designToSave: CustomDesignState = {
      ...design,
      sides: {
        front: { ...design.sides.front },
        back: { ...design.sides.back },
      },
    };
    let uploadedArtwork = false;

    setError('');
    setIsUploading(true);
    onUploadingChange?.(true);

    try {
      for (const side of ['front', 'back'] as CustomizerSide[]) {
        const file = pendingArtworkFiles?.[side];
        if (!file || !sideHasDesign(designToSave.sides[side])) continue;

        const uploaded = await uploadCustomizerArtwork(file);
        uploadedArtwork = true;
        designToSave = {
          ...designToSave,
          sides: {
            ...designToSave.sides,
            [side]: {
              ...designToSave.sides[side],
              artworkUrl: uploaded.url,
              artworkFilename: uploaded.filename,
              artworkKind: isImageArtwork(uploaded.filename) ? 'image' : 'file',
            },
          },
        };
        onPendingArtworkChange?.(side, null);
      }

      const storable = stripTemporaryArtwork(designToSave);
      window.localStorage.setItem(storageKeyForProduct(product.id), JSON.stringify(storable.design));
      onChange(storable.design);
      setSavedDesignAvailable(true);
      showSaveMessage(uploadedArtwork ? 'Design saved with artwork' : storable.removedArtwork ? 'Layout saved; upload artwork again' : 'Design saved');
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : 'Save failed. Please try again.');
      showSaveMessage('Save failed');
    } finally {
      setIsUploading(false);
      onUploadingChange?.(false);
    }
  };

  const loadSavedDesign = () => {
    if (!canUseStorage()) return;

    try {
      const saved = window.localStorage.getItem(storageKeyForProduct(product.id));
      if (!saved) return;
      const parsed = normalizeSavedDesign(JSON.parse(saved), design);
      if (!parsed) {
        showSaveMessage('Saved design invalid');
        return;
      }
      onChange(parsed);
      showSaveMessage('Design loaded');
    } catch {
      showSaveMessage('Load failed');
    }
  };

  const hasAnyDesign = hasCustomDesign(design);

  return (
    <div id="product-customizer" className="scroll-mt-36 border border-[var(--color-border)] bg-white">
      <div className="border-b border-[var(--color-border)] px-4 py-4 sm:px-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <span className="text-sm font-semibold uppercase tracking-wider text-black">Design tools</span>
            <p className="mt-1 text-sm text-[var(--color-text-secondary)]">
              {product.title}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {saveMessage && (
              <span className="text-xs font-semibold uppercase tracking-wide text-[var(--color-text-secondary)]" aria-live="polite">
                {saveMessage}
              </span>
            )}
            {savedDesignAvailable && (
              <button
                type="button"
                onClick={loadSavedDesign}
                className="border border-[var(--color-border)] px-3 py-2 text-xs font-semibold uppercase tracking-wide text-black hover:border-black"
              >
                Load saved
              </button>
            )}
            <button
              type="button"
              disabled={!canEditActiveSide || isUploading}
              onClick={() => inputRef.current?.click()}
              className="border border-brand-blue bg-brand-blue px-3 py-2 text-xs font-semibold uppercase tracking-wide text-white hover:bg-brand-blue-hover disabled:cursor-not-allowed disabled:border-gray-300 disabled:bg-gray-300 disabled:text-gray-500 lg:hidden"
            >
              Upload artwork
            </button>
            <button
              type="button"
              disabled={!hasAnyDesign || isUploading}
              onClick={saveDesign}
              className="border border-[var(--color-border)] px-3 py-2 text-xs font-semibold uppercase tracking-wide text-black hover:border-black disabled:cursor-not-allowed disabled:opacity-40"
            >
              {isUploading ? 'Saving...' : 'Save design'}
            </button>
            {hasAnyDesign && (
              <button
                type="button"
                onClick={() => {
                  onClearPendingArtwork?.();
                  onChange((current) => createDefaultCustomDesign(current.profile));
                }}
                className="border border-[var(--color-border)] px-3 py-2 text-xs font-semibold uppercase tracking-wide text-black hover:border-black"
              >
                Clear all
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="grid gap-4 p-3 sm:gap-5 sm:p-5 lg:grid-cols-[minmax(0,1fr)_300px]">
        <div>
          <div className="relative mx-auto aspect-[3/4] w-full max-w-[340px] overflow-hidden bg-[var(--color-off-white)] sm:max-w-none">
            {previewImageUrl ? (
              <Image
                src={previewImageUrl}
                alt={previewImageAlt || product.title}
                fill
                className="object-cover"
                sizes="(max-width: 1024px) 100vw, 45vw"
              />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center text-sm text-gray-500">
                No product image
              </div>
            )}
            <CustomizerPreviewOverlay
              design={design}
              side={activeSide}
              showBoundary
              editable
              dragTarget={activeMoveTarget}
              onArtworkPositionChange={(x, y) => updateSide({ x, y })}
              onTextPositionChange={(textX, textY) => updateSide({ textX, textY })}
            />
          </div>
          <div className="mt-3 flex flex-wrap items-center justify-between gap-3 text-xs text-[var(--color-text-secondary)]">
            <div className="flex flex-wrap items-center gap-2">
              <span className="border border-[var(--color-border)] px-2 py-1">{activePrintArea.label}</span>
              <span className="border border-[var(--color-border)] px-2 py-1">{getDesignSideLabel(design, activeSide)}</span>
            </div>
            {(canMoveArtwork || canMoveText) && (
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  disabled={!canMoveArtwork}
                  aria-pressed={activeMoveTarget === 'artwork'}
                  onClick={() => setMoveTarget('artwork')}
                  className={`border px-2 py-1 text-[11px] font-semibold uppercase tracking-wide transition-colors ${
                    activeMoveTarget === 'artwork'
                      ? 'border-brand-blue bg-brand-blue text-white'
                      : 'border-[var(--color-border)] bg-white text-black hover:border-black disabled:cursor-not-allowed disabled:text-gray-400 disabled:opacity-50'
                  }`}
                >
                  Move artwork
                </button>
                <button
                  type="button"
                  disabled={!canMoveText}
                  aria-pressed={activeMoveTarget === 'text'}
                  onClick={() => setMoveTarget('text')}
                  className={`border px-2 py-1 text-[11px] font-semibold uppercase tracking-wide transition-colors ${
                    activeMoveTarget === 'text'
                      ? 'border-brand-blue bg-brand-blue text-white'
                      : 'border-[var(--color-border)] bg-white text-black hover:border-black disabled:cursor-not-allowed disabled:text-gray-400 disabled:opacity-50'
                  }`}
                >
                  Move text
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="space-y-5">
          <div>
            <span className="mb-2 block text-xs font-semibold uppercase tracking-wider text-black">Side</span>
            <div className="grid grid-cols-2 gap-2">
              {(['front', 'back'] as CustomizerSide[]).map((side) => {
                const disabled = !availableSides[side];
                const selected = activeSide === side;
                return (
                  <button
                    key={side}
                    type="button"
                    disabled={disabled}
                    aria-pressed={selected}
                    onClick={() => updateDesign({ activeSide: side })}
                    className={`border px-3 py-2 text-sm font-semibold transition-colors ${
                      selected
                        ? 'border-brand-blue bg-brand-blue text-white'
                        : disabled
                          ? 'cursor-not-allowed border-gray-200 bg-gray-100 text-gray-400'
                          : 'border-[var(--color-border)] bg-white text-black hover:border-black'
                    }`}
                  >
                    {getDesignSideLabel(design, side)}
                  </button>
                );
              })}
            </div>
            {(['front', 'back'] as CustomizerSide[]).map((side) => (
              !availableSides[side] && sideAvailabilityNotes?.[side] ? (
                <p key={side} className="mt-2 text-xs text-amber-700">
                  {sideAvailabilityNotes[side]}
                </p>
              ) : null
            ))}
            {availableSides[otherSide] && sideHasDesign(design.sides[activeSide]) && !sideHasDesign(design.sides[otherSide]) && (
              <button
                type="button"
                onClick={duplicateActiveSideToOtherSide}
                className="mt-2 text-xs font-semibold uppercase tracking-wide text-[var(--color-text-secondary)] underline hover:text-black"
              >
                Duplicate to {getDesignSideLabel(design, otherSide)}
              </button>
            )}
          </div>

          <div>
            <span className="mb-2 block text-xs font-semibold uppercase tracking-wider text-black">Upload the Artwork NOT a Mock-up, Please</span>
            <Image
              src="/images/artwork-vs-mockup.png"
              alt="Upload the artwork file (left, with green check), not a mock-up of the product (right, with red X)"
              width={1536}
              height={1024}
              sizes="(min-width: 640px) 28rem, 100vw"
              className="mb-3 hidden h-auto w-full max-w-[28rem] border border-[var(--color-border)] sm:block"
            />
            <div
              onDragOver={(event) => {
                event.preventDefault();
                setIsDraggingFile(true);
              }}
              onDragLeave={() => setIsDraggingFile(false)}
              onDrop={handleDrop}
              onClick={() => {
                if (!isUploading && canEditActiveSide) inputRef.current?.click();
              }}
              onKeyDown={handleUploadKeyDown}
              role="button"
              tabIndex={0}
              aria-disabled={isUploading || !canEditActiveSide}
              aria-label={`Upload artwork for ${getDesignSideLabel(design, activeSide)}`}
              className={`cursor-pointer border border-dashed px-4 py-4 text-center transition-colors ${
                isDraggingFile ? 'border-black bg-gray-50' : 'border-[var(--color-border)] hover:border-black'
              } ${isUploading || !canEditActiveSide ? 'pointer-events-none opacity-60' : ''} focus:border-black focus:outline-none`}
            >
              <input
                ref={inputRef}
                type="file"
                accept=".png,.jpg,.jpeg,.webp,.svg,.pdf,.ai,.eps"
                disabled={!canEditActiveSide}
                onChange={handleFileSelect}
                className="hidden"
              />
              {isUploading ? (
                <div className="flex items-center justify-center gap-2 text-sm text-[var(--color-text-secondary)]">
                  <svg className="h-4 w-4 animate-spin text-black" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Uploading...
                </div>
              ) : activeDesign.artworkUrl ? (
                <div className="min-w-0 text-sm">
                  <p className="truncate font-semibold text-black">{activeDesign.artworkFilename}</p>
                  <p className="text-xs text-[var(--color-text-secondary)]">
                    {getArtworkKindLabel(activeDesign)} - Replace artwork for {getDesignSideLabel(design, activeSide)}
                  </p>
                </div>
              ) : (
                <div className="text-sm">
                  <p className="font-semibold text-black">Upload image or print file</p>
                  <p className="mt-1 text-xs text-[var(--color-text-secondary)]">PNG, JPG, WEBP, SVG, PDF, AI, EPS up to {formatSize(MAX_SIZE)}</p>
                </div>
              )}
            </div>
            {error && <p className="mt-2 text-sm text-red-600" aria-live="polite">{error}</p>}
            {activeDesign.artworkWarnings.length > 0 && (
              <div className="mt-2 border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800" aria-live="polite">
                <p className="font-semibold uppercase tracking-wide text-amber-900">Artwork check</p>
                <ul className="mt-1 list-disc space-y-1 pl-4">
                  {activeDesign.artworkWarnings.map((warning) => (
                    <li key={warning}>{warning}</li>
                  ))}
                </ul>
              </div>
            )}
            {activeDesign.artworkUrl && (
              <button
                type="button"
                onClick={removeArtwork}
                className="mt-2 text-xs font-semibold uppercase tracking-wide text-[var(--color-text-secondary)] underline hover:text-black"
              >
                Remove artwork
              </button>
            )}
          </div>

          <div className="space-y-4">
            <span className="block text-xs font-semibold uppercase tracking-wider text-black">Artwork placement</span>
            <div>
              <div className="mb-2 flex items-center justify-between">
                <label htmlFor="design-position-x" className="text-xs font-semibold uppercase tracking-wider text-black">Horizontal</label>
                <span className="text-xs text-[var(--color-text-secondary)]">{Math.round(activeDesign.x)}%</span>
              </div>
              <input
                id="design-position-x"
                type="range"
                min="0"
                max="100"
                value={activeDesign.x}
                disabled={!canEditActiveSide || !activeDesign.artworkUrl}
                onChange={(event) => updateSide({ x: Number(event.target.value) })}
                className="w-full accent-black disabled:opacity-40"
              />
            </div>

            <div>
              <div className="mb-2 flex items-center justify-between">
                <label htmlFor="design-position-y" className="text-xs font-semibold uppercase tracking-wider text-black">Vertical</label>
                <span className="text-xs text-[var(--color-text-secondary)]">{Math.round(activeDesign.y)}%</span>
              </div>
              <input
                id="design-position-y"
                type="range"
                min="0"
                max="100"
                value={activeDesign.y}
                disabled={!canEditActiveSide || !activeDesign.artworkUrl}
                onChange={(event) => updateSide({ y: Number(event.target.value) })}
                className="w-full accent-black disabled:opacity-40"
              />
            </div>

            <div>
              <div className="mb-2 flex items-center justify-between">
                <label htmlFor="design-scale" className="text-xs font-semibold uppercase tracking-wider text-black">Scale</label>
                <span className="text-xs text-[var(--color-text-secondary)]">{Math.round(activeDesign.scale)}%</span>
              </div>
              <input
                id="design-scale"
                type="range"
                min="20"
                max="140"
                value={activeDesign.scale}
                disabled={!canEditActiveSide || !activeDesign.artworkUrl}
                onChange={(event) => updateSide({ scale: Number(event.target.value) })}
                className="w-full accent-black disabled:opacity-40"
              />
            </div>

            <div>
              <div className="mb-2 flex items-center justify-between">
                <label htmlFor="design-rotation" className="text-xs font-semibold uppercase tracking-wider text-black">Rotate</label>
                <span className="text-xs text-[var(--color-text-secondary)]">{Math.round(activeDesign.rotation)}deg</span>
              </div>
              <input
                id="design-rotation"
                type="range"
                min="-45"
                max="45"
                value={activeDesign.rotation}
                disabled={!canEditActiveSide || !activeDesign.artworkUrl}
                onChange={(event) => updateSide({ rotation: Number(event.target.value) })}
                className="w-full accent-black disabled:opacity-40"
              />
            </div>

            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                disabled={!canEditActiveSide || !activeDesign.artworkUrl}
                onClick={() => updateSide({ x: 50, y: 48, rotation: 0 })}
                className="border border-[var(--color-border)] px-3 py-2 text-xs font-semibold uppercase tracking-wide text-black hover:border-black disabled:cursor-not-allowed disabled:opacity-40"
              >
                Centre
              </button>
              <button
                type="button"
                disabled={!canEditActiveSide || !activeDesign.artworkUrl}
                onClick={() => updateSide({ scale: 58, rotation: 0 })}
                className="border border-[var(--color-border)] px-3 py-2 text-xs font-semibold uppercase tracking-wide text-black hover:border-black disabled:cursor-not-allowed disabled:opacity-40"
              >
                Reset
              </button>
            </div>
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between gap-3">
              <label htmlFor="custom-text" className="block text-xs font-semibold uppercase tracking-wider text-black">Text</label>
              {activeDesign.text.trim() && (
                <button
                  type="button"
                  onClick={clearText}
                  className="text-xs font-semibold uppercase tracking-wide text-[var(--color-text-secondary)] underline hover:text-black"
                >
                  Clear text
                </button>
              )}
            </div>
            <textarea
              id="custom-text"
              maxLength={120}
              rows={2}
              value={activeDesign.text}
              disabled={!canEditActiveSide}
              onChange={(event) => {
                const text = event.target.value;
                const patch: Partial<typeof activeDesign> = { text };
                if (text.trim() && !activeDesign.text.trim()) {
                  setMoveTarget('text');
                  if (activeDesign.artworkUrl) {
                    patch.textX = activeDesign.x;
                    patch.textY = clamp(activeDesign.y + 23, 4, 96);
                  }
                }
                updateSide(patch);
              }}
              placeholder="Optional name, slogan, number, or short message"
              className="w-full resize-none border border-[var(--color-border)] px-3 py-2 text-sm focus:border-black focus:outline-none"
            />
            <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-wrap items-center gap-2">
                {TEXT_COLORS.map((color) => (
                  <button
                    key={color}
                    type="button"
                    disabled={!canEditActiveSide}
                    onClick={() => updateSide({ textColor: color })}
                    className={`h-7 w-7 border ${activeDesign.textColor === color ? 'border-black ring-2 ring-black/20' : 'border-[var(--color-border)]'}`}
                    style={{ backgroundColor: color }}
                    aria-label={`Text colour ${color}`}
                  />
                ))}
                <input
                  type="color"
                  value={activeDesign.textColor}
                  disabled={!canEditActiveSide}
                  onChange={(event) => updateSide({ textColor: event.target.value })}
                  className="h-7 w-7 cursor-pointer rounded-none border border-[var(--color-border)] bg-white p-0"
                  aria-label="Custom text colour"
                />
              </div>
              <input
                type="number"
                min="12"
                max="48"
                value={activeDesign.textSize}
                disabled={!canEditActiveSide}
                onChange={(event) => updateSide({ textSize: clamp(Number(event.target.value), 12, 48) })}
                className="w-16 border border-[var(--color-border)] px-2 py-1 text-sm focus:border-black focus:outline-none"
                aria-label="Text size"
              />
            </div>
            <div className="mt-3 grid grid-cols-[minmax(0,1fr)_auto] gap-2">
              <label htmlFor="text-font" className="sr-only">Text font</label>
              <select
                id="text-font"
                value={activeDesign.textFont}
                disabled={!canEditActiveSide}
                onChange={(event) => updateSide({ textFont: event.target.value as TextFontId })}
                className="w-full rounded-none border border-[var(--color-border)] bg-white px-3 py-2 text-sm text-black focus:border-black focus:outline-none"
              >
                {TEXT_FONT_OPTIONS.map((font) => (
                  <option key={font.id} value={font.id}>
                    {font.label}
                  </option>
                ))}
              </select>
              <div className="grid grid-cols-2 gap-1">
                <button
                  type="button"
                  aria-label="Toggle bold text"
                  aria-pressed={activeDesign.textBold}
                  disabled={!canEditActiveSide}
                  onClick={() => updateSide({ textBold: !activeDesign.textBold })}
                  className={`h-10 w-10 border text-sm font-bold transition-colors ${
                    activeDesign.textBold
                      ? 'border-brand-blue bg-brand-blue text-white'
                      : 'border-[var(--color-border)] bg-white text-black hover:border-black'
                  }`}
                >
                  B
                </button>
                <button
                  type="button"
                  aria-label="Toggle italic text"
                  aria-pressed={activeDesign.textItalic}
                  disabled={!canEditActiveSide}
                  onClick={() => updateSide({ textItalic: !activeDesign.textItalic })}
                  className={`h-10 w-10 border text-sm italic transition-colors ${
                    activeDesign.textItalic
                      ? 'border-brand-blue bg-brand-blue text-white'
                      : 'border-[var(--color-border)] bg-white text-black hover:border-black'
                  }`}
                >
                  I
                </button>
              </div>
            </div>
            <div className="mt-3 grid grid-cols-3 gap-1">
              {TEXT_ALIGN_OPTIONS.map((align) => (
                <button
                  key={align.id}
                  type="button"
                  aria-pressed={activeDesign.textAlign === align.id}
                  disabled={!canEditActiveSide}
                  onClick={() => updateSide({ textAlign: align.id })}
                  className={`border px-2 py-2 text-xs font-semibold uppercase tracking-wide transition-colors ${
                    activeDesign.textAlign === align.id
                      ? 'border-brand-blue bg-brand-blue text-white'
                      : 'border-[var(--color-border)] bg-white text-black hover:border-black'
                  }`}
                >
                  {align.label}
                </button>
              ))}
            </div>
            <div className="mt-4 space-y-3">
              <span className="block text-xs font-semibold uppercase tracking-wider text-black">Text placement</span>
              <div>
                <div className="mb-2 flex items-center justify-between">
                  <label htmlFor="text-position-x" className="text-xs font-semibold uppercase tracking-wider text-black">Horizontal</label>
                  <span className="text-xs text-[var(--color-text-secondary)]">{Math.round(activeDesign.textX)}%</span>
                </div>
                <input
                  id="text-position-x"
                  type="range"
                  min="0"
                  max="100"
                  value={activeDesign.textX}
                  disabled={!canEditActiveSide || !activeDesign.text.trim()}
                  onChange={(event) => updateSide({ textX: Number(event.target.value) })}
                  className="w-full accent-black disabled:opacity-40"
                />
              </div>
              <div>
                <div className="mb-2 flex items-center justify-between">
                  <label htmlFor="text-position-y" className="text-xs font-semibold uppercase tracking-wider text-black">Vertical</label>
                  <span className="text-xs text-[var(--color-text-secondary)]">{Math.round(activeDesign.textY)}%</span>
                </div>
                <input
                  id="text-position-y"
                  type="range"
                  min="0"
                  max="100"
                  value={activeDesign.textY}
                  disabled={!canEditActiveSide || !activeDesign.text.trim()}
                  onChange={(event) => updateSide({ textY: Number(event.target.value) })}
                  className="w-full accent-black disabled:opacity-40"
                />
              </div>
              <div>
                <div className="mb-2 flex items-center justify-between">
                  <label htmlFor="text-rotation" className="text-xs font-semibold uppercase tracking-wider text-black">Rotate</label>
                  <span className="text-xs text-[var(--color-text-secondary)]">{Math.round(activeDesign.textRotation)}deg</span>
                </div>
                <input
                  id="text-rotation"
                  type="range"
                  min="-45"
                  max="45"
                  value={activeDesign.textRotation}
                  disabled={!canEditActiveSide || !activeDesign.text.trim()}
                  onChange={(event) => updateSide({ textRotation: Number(event.target.value) })}
                  className="w-full accent-black disabled:opacity-40"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  disabled={!canEditActiveSide || !activeDesign.text.trim()}
                  onClick={() => updateSide({ textX: 50, textY: activeDesign.artworkUrl ? 72 : 50, textRotation: 0 })}
                  className="border border-[var(--color-border)] px-3 py-2 text-xs font-semibold uppercase tracking-wide text-black hover:border-black disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Centre text
                </button>
                <button
                  type="button"
                  disabled={!canEditActiveSide || !activeDesign.text.trim()}
                  onClick={() => updateSide({ textSize: 24, textRotation: 0, textAlign: 'center' })}
                  className="border border-[var(--color-border)] px-3 py-2 text-xs font-semibold uppercase tracking-wide text-black hover:border-black disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Reset text
                </button>
              </div>
            </div>
          </div>

          <div>
            <label htmlFor="special-instructions" className="mb-2 block text-xs font-semibold uppercase tracking-wider text-black">
              Special instructions
            </label>
            <textarea
              id="special-instructions"
              maxLength={240}
              rows={3}
              value={design.instructions}
              onChange={(event) => updateDesign({ instructions: event.target.value })}
              placeholder="Placement notes, colour matching, or production details"
              className="w-full resize-none border border-[var(--color-border)] px-3 py-2 text-sm focus:border-black focus:outline-none"
            />
          </div>

          <button
            type="button"
            disabled={!canEditActiveSide || !sideHasDesign(activeDesign)}
            onClick={removeSideDesign}
            className="w-full border border-[var(--color-border)] px-3 py-2 text-xs font-semibold uppercase tracking-wide text-black hover:border-black disabled:cursor-not-allowed disabled:opacity-40"
          >
            Remove {getDesignSideLabel(design, activeSide)} design
          </button>
        </div>
      </div>
    </div>
  );
}
